import React from "react";
import PropTypes from "prop-types";
import { withStyles } from "@material-ui/core/styles";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Button,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  CircularProgress,
  Snackbar,
  Chip,
  Grid,
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import Autocomplete from "@material-ui/lab/Autocomplete";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import SearchIcon from "@material-ui/icons/Search";
import VisibilityIcon from "@material-ui/icons/Visibility";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import utils from "../../universal/utils";
import {
  collectCertificateSizes,
  formatCertificateSizes,
  certificateMatchesSize,
} from "../../universal/eceCertificates";

const styles = (theme) => ({
  root: {
    width: "100%",
  },
  filterBar: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
    alignItems: "center",
    flexWrap: "wrap",
  },
  addButton: {
    marginLeft: "auto",
  },
  tableContainer: {
    maxHeight: 600,
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(4),
  },
  dialogContent: {
    minWidth: 600,
  },
  formField: {
    marginBottom: theme.spacing(2),
  },
  rowsTable: {
    marginTop: theme.spacing(2),
  },
  rowItem: {
    display: "flex",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
    alignItems: "center",
  },
  addRowButton: {
    marginTop: theme.spacing(1),
  },
  certificateOption: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
  },
  certificateMainInfo: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  certificateDetails: {
    fontSize: "0.85rem",
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  eceIdBadge: {
    backgroundColor: theme.palette.primary.main,
    color: "white",
    padding: "2px 8px",
    borderRadius: 4,
    fontSize: "0.75rem",
    fontWeight: 600,
  },
  selectedOption: {
    backgroundColor: theme.palette.action.selected,
  },
  selectedIndicator: {
    color: theme.palette.success.main,
    marginLeft: "auto",
  },
  currentSelectionBox: {
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
  },
  currentSelectionLabel: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
  },
  autoMatchBadge: {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.contrastText,
    padding: "2px 6px",
    borderRadius: 4,
    fontSize: "0.7rem",
    fontWeight: 600,
    marginRight: theme.spacing(1),
  },
  hiddenInput: {
    display: "none",
  },
  uploadControl: {
    display: "inline-flex",
    alignItems: "center",
  },
  parserActions: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    alignItems: "center",
  },
  matchReason: {
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
});

const hebrewLabels = {
  title: "ניהול חשבוניות ECE",
  filters: {
    invoiceId: "מספר חשבונית",
    search: "חיפוש",
  },
  table: {
    invoiceId: "מספר חשבונית",
    rowCount: "מספר שורות",
    actions: "פעולות",
  },
  buttons: {
    add: "הוסף חשבונית חדשה",
    edit: "עריכה",
    delete: "מחיקה",
    view: "צפייה",
    save: "שמור",
    cancel: "ביטול",
    addRow: "הוסף שורה",
    removeRow: "הסר",
    parseInvoice: "נתח חשבונית",
    parsePdf: "נתח PDF עם AI",
    uploadPdf: "העלה PDF",
    clearInvoiceText: "נקה",
    clearPdf: "הסר PDF",
  },
  dialog: {
    create: "יצירת חשבונית ECE חדשה",
    edit: "עריכת חשבונית ECE",
    view: "צפייה בחשבונית ECE",
    delete: "מחיקת חשבונית ECE",
    deleteConfirm: "האם אתה בטוח שברצונך למחוק חשבונית זו?",
  },
  messages: {
    loading: "טוען...",
    noData: "לא נמצאו חשבוניות",
    success: "הפעולה בוצעה בהצלחה",
    error: "אירעה שגיאה",
    parsingInvoice: "מנתח חשבונית ומחפש תעודות...",
    parsingPdf: "שולח PDF ל-OpenAI ומתאים תעודות ECE...",
    parseJobTimeout: "הניתוח לקח יותר מדי זמן. נסו שוב, או בדקו את לוגים של edge.",
    invoiceParsed: "נמצאו {0} שורות",
    matchedCertificates: "{0} תעודות הותאמו אוטומטית",
    pdfRequired: "אנא העלה קובץ PDF",
    pdfInvalid: "יש להעלות קובץ PDF בלבד",
    pdfTooLarge: "הקובץ גדול מדי (מקסימום 15MB)",
  },
  fields: {
    invoiceId: "מספר חשבונית",
    rows: "שורות חשבונית",
    line: "שורה",
    name: "שם המוצר",
    size: "מידה (כולל מדד עומס/מהירות)",
    eceId: "מספר ECE",
    invoiceText: "טקסט חשבונית (הדבק מ-ChatGPT או PDF)",
    invoiceTextHelper: "הדבק את טקסט החשבונית כאן. המערכת תנסה למצוא אוטומטית את תעודות ה-ECE המתאימות לכל שורה.",
    invoicePdf: "חשבונית PDF (OpenAI)",
    invoicePdfHelper: "העלה את קובץ החשבונית. המערכת תשלח אותו ל-OpenAI, תחלץ את השורות ותתאים תעודות ECE לפי יצרן, מידה ודגם (שדה goodfor — לאיזה מודלים התעודה מתאימה).",
  },
};

const basePath = utils.getEdgeBasePath();

class EceInvoicesManager extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      invoices: [],
      loading: true,
      pagination: {
        page: 0,
        limit: 20,
        total: 0,
      },
      filters: {
        invoiceId: "",
      },
      dialogOpen: false,
      dialogMode: "create", // create, edit, or view
      currentInvoice: {
        invoiceId: "",
        rows: [],
      },
      deleteDialogOpen: false,
      invoiceToDelete: null,
      snackbar: {
        open: false,
        message: "",
        severity: "success",
      },
      // Certificate autocomplete data
      availableCertificates: [],
      certificatesLoading: false,
      // Invoice text parsing
      invoiceText: "",
      parsingInvoice: false,
      pdfFileData: null,
      pdfFileName: "",
      parsingPdf: false,
      parseWarnings: [],
    };
  }

  componentDidMount() {
    this.loadInvoices();
    this.loadCertificates();
  }

  loadCertificates = async () => {
    this.setState({ certificatesLoading: true });
    
    try {
      // Load all certificates (up to 1000) for the autocomplete
      const response = await fetch(
        `${basePath}/api/v1/admin/ece/certificates?page=1&limit=1000`
      );
      const data = await response.json();

      if (data.ok) {
        this.setState({
          availableCertificates: data.certificates || [],
        });
      }
    } catch (error) {
      console.error("Error loading certificates:", error);
    } finally {
      this.setState({ certificatesLoading: false });
    }
  };

  loadInvoices = async () => {
    this.setState({ loading: true });

    try {
      const { pagination, filters } = this.state;
      const queryParams = new URLSearchParams({
        page: pagination.page + 1,
        limit: pagination.limit,
      });

      if (filters.invoiceId) queryParams.append("invoiceId", filters.invoiceId);

      const response = await fetch(
        `${basePath}/api/v1/admin/ece/invoices?${queryParams.toString()}`
      );
      const data = await response.json();

      if (data.ok) {
        this.setState({
          invoices: data.invoices,
          pagination: {
            ...pagination,
            total: data.pagination.total,
          },
        });
      }
    } catch (error) {
      console.error("Error loading invoices:", error);
      this.showSnackbar("שגיאה בטעינת החשבוניות", "error");
    } finally {
      this.setState({ loading: false });
    }
  };

  handlePageChange = (event, newPage) => {
    this.setState(
      (prevState) => ({
        pagination: { ...prevState.pagination, page: newPage },
      }),
      () => this.loadInvoices()
    );
  };

  handleRowsPerPageChange = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    this.setState(
      (prevState) => ({
        pagination: { ...prevState.pagination, limit: newLimit, page: 0 },
      }),
      () => this.loadInvoices()
    );
  };

  handleFilterChange = (field) => (event) => {
    // Extract value before setState to avoid synthetic event reuse issue
    const value = event.target.value;
    
    this.setState((prevState) => ({
      filters: {
        ...prevState.filters,
        [field]: value,
      },
    }));
  };

  handleSearch = () => {
    this.setState(
      (prevState) => ({
        pagination: { ...prevState.pagination, page: 0 },
      }),
      () => this.loadInvoices()
    );
  };

  openCreateDialog = () => {
    // Reload certificates when opening dialog to ensure fresh data
    this.loadCertificates();
    
    this.setState({
      dialogOpen: true,
      dialogMode: "create",
      currentInvoice: {
        invoiceId: "",
        rows: [
          {
            line: 1,
            name: "",
            size: "",
            eceId: "",
            checked: false,
          },
        ],
      },
      invoiceText: "",
      parsingInvoice: false,
      pdfFileData: null,
      pdfFileName: "",
      parsingPdf: false,
      parseWarnings: [],
    });
  };

  openEditDialog = (invoice) => {
    // Reload certificates when opening dialog to ensure fresh data
    this.loadCertificates();
    
    this.setState({
      dialogOpen: true,
      dialogMode: "edit",
      currentInvoice: {
        invoiceId: invoice.invoiceId,
        rows: invoice.invoice?.rows || [],
      },
    });
  };

  openViewDialog = (invoice) => {
    this.setState({
      dialogOpen: true,
      dialogMode: "view",
      currentInvoice: {
        invoiceId: invoice.invoiceId,
        rows: invoice.invoice?.rows || [],
      },
    });
  };

  openDeleteDialog = (invoice) => {
    this.setState({
      deleteDialogOpen: true,
      invoiceToDelete: invoice,
    });
  };

  closeDialog = () => {
    this.setState({
      dialogOpen: false,
      invoiceText: "",
      pdfFileData: null,
      pdfFileName: "",
      parsingPdf: false,
      parseWarnings: [],
    });
  };

  closeDeleteDialog = () => {
    this.setState({ deleteDialogOpen: false, invoiceToDelete: null });
  };

  handleInvoiceTextChange = (event) => {
    const value = event.target.value;
    this.setState({ invoiceText: value });
  };

  clearInvoiceText = () => {
    this.setState({ invoiceText: "" });
  };

  readFileAsDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsDataURL(file);
    });

  handlePdfFileChange = async (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;

    const isPdf =
      file.type === "application/pdf" ||
      String(file.name || "").toLowerCase().endsWith(".pdf");
    if (!isPdf) {
      this.showSnackbar(hebrewLabels.messages.pdfInvalid, "warning");
      event.target.value = "";
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      this.showSnackbar(hebrewLabels.messages.pdfTooLarge, "warning");
      event.target.value = "";
      return;
    }

    try {
      const pdfFileData = await this.readFileAsDataUrl(file);
      this.setState({
        pdfFileData,
        pdfFileName: file.name,
        parseWarnings: [],
      });
    } catch (error) {
      console.error("Error reading PDF:", error);
      this.showSnackbar("שגיאה בקריאת קובץ ה-PDF", "error");
    } finally {
      event.target.value = "";
    }
  };

  clearPdfFile = () => {
    this.setState({
      pdfFileData: null,
      pdfFileName: "",
      parseWarnings: [],
    });
  };

  parseInvoicePdf = async () => {
    const { pdfFileData, pdfFileName } = this.state;
    if (!pdfFileData) {
      this.showSnackbar(hebrewLabels.messages.pdfRequired, "warning");
      return;
    }

    this.setState({ parsingPdf: true, parseWarnings: [] });

    try {
      const response = await fetch(
        `${basePath}/api/v1/admin/ece/invoices/parse-pdf`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...utils.getAdminAuthHeaders(),
          },
          body: JSON.stringify({
            fileData: pdfFileData,
            fileName: pdfFileName,
          }),
        }
      );
      const data = await response.json();
      if (!response.ok || !data.ok) {
        throw new Error(data.error || data.message || "PDF parse failed");
      }

      const result =
        data.status === "processing" && data.jobId
          ? await this.pollParseJob(data.jobId)
          : data;

      this.applyParsedInvoice(result);
    } catch (error) {
      console.error("Error parsing PDF invoice:", error);
      this.setState({ parsingPdf: false });
      this.showSnackbar(error.message || "שגיאה בניתוח ה-PDF", "error");
    }
  };

  pollParseJob = async (jobId) => {
    const started = Date.now();
    const maxWaitMs = 4 * 60 * 1000;
    const intervalMs = 2000;

    while (Date.now() - started < maxWaitMs) {
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
      const response = await fetch(
        `${basePath}/api/v1/admin/ece/invoices/parse-pdf/${jobId}`,
        {
          headers: utils.getAdminAuthHeaders(),
        }
      );
      const data = await response.json();
      if (data.status === "processing") {
        continue;
      }
      if (!response.ok || !data.ok || data.status === "error") {
        throw new Error(data.error || data.message || "PDF parse failed");
      }
      return data;
    }

    throw new Error(hebrewLabels.messages.parseJobTimeout);
  };

  applyParsedInvoice = (data) => {
    const rows = (data.rows || []).map((row, index) => ({
      line: index + 1,
      name: row.name || "",
      size: row.size || "",
      eceId: row.eceId || "",
      checked: false,
      quantity: row.quantity,
      autoMatched: Boolean(row.autoMatched && row.eceId),
      matchReason: row.matchReason || "",
    }));

    this.setState(
      (prevState) => ({
        currentInvoice: {
          ...prevState.currentInvoice,
          invoiceId:
            prevState.currentInvoice.invoiceId || data.invoiceId || "",
          rows,
        },
        parsingPdf: false,
        parseWarnings: data.warnings || [],
      }),
      () => {
        const matchedCount =
          data.matchedCount != null
            ? data.matchedCount
            : rows.filter((row) => row.eceId).length;
        const message = `${hebrewLabels.messages.invoiceParsed.replace(
          "{0}",
          rows.length
        )} | ${hebrewLabels.messages.matchedCertificates.replace(
          "{0}",
          matchedCount
        )}`;
        this.showSnackbar(message, "success");
      }
    );
  };

  parseInvoiceText = () => {
    const { invoiceText, availableCertificates } = this.state;
    
    if (!invoiceText.trim()) {
      this.showSnackbar("אנא הזן טקסט חשבונית", "warning");
      return;
    }

    this.setState({ parsingInvoice: true });

    // Parse the invoice text to extract line items
    const lines = invoiceText.split('\n').filter(line => line.trim());
    const productLines = [];

    // Improved regex pattern to match tire product lines with various formats
    // More flexible pattern that handles leading/trailing whitespace
    // Matches patterns like:
    // - "Michelin Road 5 180/55ZR17 73W 10 €117.58 €1,175.80"
    // - "Pirelli Rosso IV 120/70ZR17 58W 30 €97.67 €2,930.10"
    // - "Metzeler M9 RR 160/60ZR17 69W 5 €118.31 €591.55"
    // Capture everything up to the tire size pattern (which starts with digits)
    const tirePattern = /^\s*(.+?)\s+(\d{2,3}[\/\-]\d{2,3}[A-Z\-]*\d{2}(?:\s+\d{2,3}[A-Z]+)?)\s+(\d+)\s+[€$£]/;

    console.log('Parsing invoice with', lines.length, 'lines');
    console.log('Available certificates:', availableCertificates.length);

    for (const line of lines) {
      const trimmedLine = line.trim();
      const match = trimmedLine.match(tirePattern);
      
      if (match) {
        const fullProductName = match[1].trim();
        const sizeWithRating = match[2].trim();
        const quantity = parseInt(match[3]);

        // Extract brand and model from product name
        const { brand, model } = this.extractBrandAndModel(fullProductName);

        console.log('Parsed line:', { fullProductName, brand, model, size: sizeWithRating, quantity });

        if (brand && sizeWithRating) {
          productLines.push({
            name: fullProductName,
            brand,
            model,
            size: sizeWithRating,
            quantity,
            originalLine: line,
          });
        } else {
          console.warn('No brand or size found for:', fullProductName);
        }
      }
    }

    console.log('Total products parsed:', productLines.length);

    if (productLines.length === 0) {
      this.showSnackbar("לא נמצאו שורות מוצרים תקינות בטקסט", "warning");
      this.setState({ parsingInvoice: false });
      return;
    }

    // Match each product line to certificates
    const rows = productLines.map((product, index) => {
      const matchedCert = this.findMatchingCertificate(product, availableCertificates);
      
      if (matchedCert) {
        console.log('Matched:', product.name, '->', matchedCert.eceId);
      } else {
        console.log('No match for:', product.name, product.size, product.brand);
      }
      
      return {
        line: index + 1,
        name: product.name,
        size: product.size,
        eceId: matchedCert ? matchedCert.eceId : "",
        checked: false,
        quantity: product.quantity,
        autoMatched: !!matchedCert, // Flag to indicate this was auto-matched
      };
    });

    const matchedCount = rows.filter(row => row.eceId).length;
    console.log('Certificate matches:', matchedCount, 'out of', rows.length);

    this.setState(
      (prevState) => ({
        currentInvoice: {
          ...prevState.currentInvoice,
          rows,
        },
        parsingInvoice: false,
        parseWarnings: [],
      }),
      () => {
        const message = `${hebrewLabels.messages.invoiceParsed.replace('{0}', productLines.length)} | ${hebrewLabels.messages.matchedCertificates.replace('{0}', matchedCount)}`;
        this.showSnackbar(message, "success");
      }
    );
  };

  extractBrandAndModel = (productName) => {
    // Common tire brands
    const brands = [
      'Michelin', 'Pirelli', 'Metzeler', 'Continental', 'Bridgestone',
      'Dunlop', 'Mitas', 'Avon', 'Shinko', 'Heidenau', 'Maxxis',
      'Kenda', 'IRC', 'Anlas', 'Sava', 'Vee Rubber'
    ];

    let brand = null;
    let model = "";

    for (const b of brands) {
      if (productName.toLowerCase().includes(b.toLowerCase())) {
        brand = b;
        // Extract model (everything after brand name)
        const brandIndex = productName.toLowerCase().indexOf(b.toLowerCase());
        model = productName.substring(brandIndex + b.length).trim();
        break;
      }
    }

    return { brand, model };
  };

  findMatchingCertificate = (product, certificates) => {
    if (!product.brand || !product.size || certificates.length === 0) {
      console.log('Cannot match - missing data:', { 
        hasBrand: !!product.brand, 
        hasSize: !!product.size, 
        certCount: certificates.length 
      });
      return null;
    }

    console.log('Looking for:', product.brand, product.size);

    let matches = certificates.filter(cert => {
      const brandMatch = cert.brand && cert.brand.toLowerCase() === product.brand.toLowerCase();
      return brandMatch && certificateMatchesSize(cert, product.size);
    });

    console.log('Size matches found:', matches.length);

    if (matches.length === 0) {
      return null;
    }

    if (product.model) {
      const modelMatches = matches.filter(cert => 
        cert.goodfor && cert.goodfor.some(gf => 
          gf.toLowerCase().includes(product.model.toLowerCase()) ||
          product.model.toLowerCase().includes(gf.toLowerCase())
        )
      );
      
      if (modelMatches.length > 0) {
        console.log('Model match found:', modelMatches[0].eceId);
        return modelMatches[0];
      }
    }

    console.log('Using first size match:', matches[0].eceId);
    return matches[0];
  };

  handleInvoiceIdChange = (event) => {
    // Extract value before setState to avoid synthetic event reuse issue
    const value = event.target.value;
    
    this.setState((prevState) => ({
      currentInvoice: {
        ...prevState.currentInvoice,
        invoiceId: value,
      },
    }));
  };

  handleRowFieldChange = (index, field) => (event) => {
    // Extract value before setState to avoid synthetic event reuse issue
    const value = event.target.value;
    
    this.setState((prevState) => {
      const newRows = [...prevState.currentInvoice.rows];
      newRows[index] = {
        ...newRows[index],
        [field]: value,
      };
      return {
        currentInvoice: {
          ...prevState.currentInvoice,
          rows: newRows,
        },
      };
    });
  };

  handleCertificateSelect = (index, certificate) => {
    this.setState((prevState) => {
      const newRows = [...prevState.currentInvoice.rows];
      
      if (certificate) {
        // Auto-fill fields from certificate
        newRows[index] = {
          ...newRows[index],
          eceId: certificate.eceId,
          size: newRows[index].size || collectCertificateSizes(certificate)[0] || certificate.size || "",
          // Suggest a name from goodfor array if name is empty
          name: newRows[index].name || (certificate.goodfor && certificate.goodfor.length > 0 
            ? `${certificate.brand} ${certificate.goodfor[0]}` 
            : newRows[index].name),
          autoMatched: false, // Clear auto-match flag on manual selection
          matchReason: "",
        };
      } else {
        // Clear eceId if certificate is cleared
        newRows[index] = {
          ...newRows[index],
          eceId: "",
          autoMatched: false,
          matchReason: "",
        };
      }
      
      return {
        currentInvoice: {
          ...prevState.currentInvoice,
          rows: newRows,
        },
      };
    });
  };

  addRow = () => {
    this.setState((prevState) => {
      const newLine = prevState.currentInvoice.rows.length + 1;
      return {
        currentInvoice: {
          ...prevState.currentInvoice,
          rows: [
            ...prevState.currentInvoice.rows,
            {
              line: newLine,
              name: "",
              size: "",
              eceId: "",
              checked: false,
              autoMatched: false,
            },
          ],
        },
      };
    });
  };

  removeRow = (index) => {
    this.setState((prevState) => {
      const newRows = prevState.currentInvoice.rows.filter((_, i) => i !== index);
      // Renumber lines
      const renumberedRows = newRows.map((row, i) => ({
        ...row,
        line: i + 1,
      }));
      return {
        currentInvoice: {
          ...prevState.currentInvoice,
          rows: renumberedRows,
        },
      };
    });
  };

  handleSave = async () => {
    const { dialogMode, currentInvoice } = this.state;

    try {
      const payload = {
        invoiceId: currentInvoice.invoiceId,
        rows: currentInvoice.rows.map((row, index) => ({
          line: index + 1,
          name: row.name,
          size: row.size,
          eceId: row.eceId,
          checked: row.checked || false,
        })),
      };

      let response;
      if (dialogMode === "create") {
        response = await fetch(`${basePath}/api/v1/admin/ece/invoices`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
      } else {
        response = await fetch(
          `${basePath}/api/v1/admin/ece/invoices/${currentInvoice.invoiceId}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          }
        );
      }

      const data = await response.json();

      if (data.ok) {
        this.showSnackbar(
          dialogMode === "create"
            ? "החשבונית נוצרה בהצלחה"
            : "החשבונית עודכנה בהצלחה",
          "success"
        );
        this.closeDialog();
        this.loadInvoices();
      } else {
        this.showSnackbar(data.message || "שגיאה בשמירת החשבונית", "error");
      }
    } catch (error) {
      console.error("Error saving invoice:", error);
      this.showSnackbar("שגיאה בשמירת החשבונית", "error");
    }
  };

  handleDelete = async () => {
    const { invoiceToDelete } = this.state;

    try {
      const response = await fetch(
        `${basePath}/api/v1/admin/ece/invoices/${invoiceToDelete.invoiceId}`,
        {
          method: "DELETE",
        }
      );

      const data = await response.json();

      if (data.ok) {
        this.showSnackbar("החשבונית נמחקה בהצלחה", "success");
        this.closeDeleteDialog();
        this.loadInvoices();
      } else {
        this.showSnackbar(data.message || "שגיאה במחיקת החשבונית", "error");
      }
    } catch (error) {
      console.error("Error deleting invoice:", error);
      this.showSnackbar("שגיאה במחיקת החשבונית", "error");
    }
  };

  showSnackbar = (message, severity = "success") => {
    this.setState({
      snackbar: {
        open: true,
        message,
        severity,
      },
    });
  };

  closeSnackbar = () => {
    this.setState((prevState) => ({
      snackbar: {
        ...prevState.snackbar,
        open: false,
      },
    }));
  };

  renderCertificateOption = (certificate, { selected }) => {
    const { classes } = this.props;
    
    return (
      <div className={classes.certificateOption} style={{ display: "flex", alignItems: "flex-start" }}>
        <div style={{ flex: 1 }}>
          <div className={classes.certificateMainInfo}>
            <span className={classes.eceIdBadge}>{certificate.eceId}</span>
            <Typography variant="body2">
              <strong>{certificate.brand}</strong> - {formatCertificateSizes(certificate)} {certificate.rating}
            </Typography>
          </div>
          {certificate.goodfor && certificate.goodfor.length > 0 && (
            <div className={classes.certificateDetails}>
              מתאים ל: {certificate.goodfor.join(", ")}
            </div>
          )}
        </div>
        {selected && (
          <CheckCircleIcon className={classes.selectedIndicator} fontSize="small" />
        )}
      </div>
    );
  };

  renderCurrentCertificate = (certificate) => {
    const { classes } = this.props;
    
    if (!certificate) return null;
    
    return (
      <Box className={classes.currentSelectionBox}>
        <Typography className={classes.currentSelectionLabel}>
          תעודה נבחרת:
        </Typography>
        <div className={classes.certificateMainInfo}>
          <span className={classes.eceIdBadge}>{certificate.eceId}</span>
          <Typography variant="body2">
            <strong>{certificate.brand}</strong> - {formatCertificateSizes(certificate)} {certificate.rating}
          </Typography>
        </div>
        {certificate.goodfor && certificate.goodfor.length > 0 && (
          <div className={classes.certificateDetails}>
            מתאים ל: {certificate.goodfor.join(", ")}
          </div>
        )}
      </Box>
    );
  };

  render() {
    const { classes } = this.props;
    const {
      invoices,
      loading,
      pagination,
      filters,
      dialogOpen,
      dialogMode,
      currentInvoice,
      deleteDialogOpen,
      invoiceToDelete,
      snackbar,
      availableCertificates,
      certificatesLoading,
      invoiceText,
      parsingInvoice,
      pdfFileData,
      pdfFileName,
      parsingPdf,
      parseWarnings,
    } = this.state;

    const isViewMode = dialogMode === "view";
    const parserBusy = parsingInvoice || parsingPdf;

    return (
      <div className={classes.root}>
        {/* Filter Bar */}
        <div className={classes.filterBar}>
          <TextField
            label={hebrewLabels.filters.invoiceId}
            value={filters.invoiceId}
            onChange={this.handleFilterChange("invoiceId")}
            variant="outlined"
            size="small"
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={<SearchIcon />}
            onClick={this.handleSearch}
          >
            {hebrewLabels.filters.search}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<AddIcon />}
            onClick={this.openCreateDialog}
            className={classes.addButton}
          >
            {hebrewLabels.buttons.add}
          </Button>
        </div>

        {/* Table */}
        {loading ? (
          <div className={classes.loadingContainer}>
            <CircularProgress />
          </div>
        ) : (
          <TableContainer component={Paper} className={classes.tableContainer}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>{hebrewLabels.table.invoiceId}</TableCell>
                  <TableCell>{hebrewLabels.table.rowCount}</TableCell>
                  <TableCell>{hebrewLabels.table.actions}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      <Typography color="textSecondary">
                        {hebrewLabels.messages.noData}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  invoices.map((invoice) => (
                    <TableRow key={invoice._id}>
                      <TableCell>
                        <Typography variant="body1" style={{ fontWeight: 600 }}>
                          {invoice.invoiceId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={`${invoice.invoice?.rows?.length || 0} שורות`}
                          color="primary"
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => this.openViewDialog(invoice)}
                          color="default"
                        >
                          <VisibilityIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => this.openEditDialog(invoice)}
                          color="primary"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => this.openDeleteDialog(invoice)}
                          color="secondary"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Pagination */}
        <TablePagination
          component="div"
          count={pagination.total}
          page={pagination.page}
          onPageChange={this.handlePageChange}
          rowsPerPage={pagination.limit}
          onRowsPerPageChange={this.handleRowsPerPageChange}
          rowsPerPageOptions={[10, 20, 50]}
        />

        {/* Create/Edit/View Dialog */}
        <Dialog
          open={dialogOpen}
          onClose={this.closeDialog}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            {dialogMode === "create" && hebrewLabels.dialog.create}
            {dialogMode === "edit" && hebrewLabels.dialog.edit}
            {dialogMode === "view" && hebrewLabels.dialog.view}
          </DialogTitle>
          <DialogContent className={classes.dialogContent}>
            <TextField
              label={hebrewLabels.fields.invoiceId}
              value={currentInvoice.invoiceId}
              onChange={this.handleInvoiceIdChange}
              fullWidth
              className={classes.formField}
              disabled={dialogMode === "edit" || isViewMode}
            />

            {/* Invoice parsers - Only show in create mode */}
            {dialogMode === "create" && (
              <Box style={{ marginBottom: 24, marginTop: 16 }}>
                <Paper style={{ padding: 16, backgroundColor: "#f5f5f5" }}>
                  <Typography variant="subtitle1" gutterBottom style={{ fontWeight: 600 }}>
                    {hebrewLabels.fields.invoicePdf}
                  </Typography>
                  <Typography variant="caption" color="textSecondary" gutterBottom style={{ display: "block", marginBottom: 12 }}>
                    {hebrewLabels.fields.invoicePdfHelper}
                  </Typography>

                  <Box className={classes.parserActions} style={{ marginBottom: 16 }}>
                    <label className={classes.uploadControl}>
                      <input
                        accept="application/pdf,.pdf"
                        className={classes.hiddenInput}
                        type="file"
                        onChange={this.handlePdfFileChange}
                        disabled={parserBusy}
                      />
                      <Button
                        variant="outlined"
                        color="primary"
                        component="span"
                        startIcon={<CloudUploadIcon />}
                        disabled={parserBusy}
                      >
                        {hebrewLabels.buttons.uploadPdf}
                      </Button>
                    </label>
                    {pdfFileName && (
                      <Typography variant="body2">
                        {pdfFileName}
                      </Typography>
                    )}
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={this.parseInvoicePdf}
                      disabled={!pdfFileData || parserBusy}
                      startIcon={parsingPdf ? <CircularProgress size={16} /> : <CloudUploadIcon />}
                    >
                      {parsingPdf ? hebrewLabels.messages.parsingPdf : hebrewLabels.buttons.parsePdf}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={this.clearPdfFile}
                      disabled={!pdfFileData || parserBusy}
                    >
                      {hebrewLabels.buttons.clearPdf}
                    </Button>
                  </Box>

                  {parseWarnings.length > 0 && (
                    <Alert severity="warning" style={{ marginBottom: 16 }}>
                      {parseWarnings.map((warning, index) => (
                        <div key={`${index}-${warning}`}>{warning}</div>
                      ))}
                    </Alert>
                  )}

                  <Typography variant="subtitle1" gutterBottom style={{ fontWeight: 600 }}>
                    {hebrewLabels.fields.invoiceText}
                  </Typography>
                  <Typography variant="caption" color="textSecondary" gutterBottom style={{ display: "block", marginBottom: 12 }}>
                    {hebrewLabels.fields.invoiceTextHelper}
                  </Typography>
                  
                  <TextField
                    multiline
                    rows={8}
                    value={invoiceText}
                    onChange={this.handleInvoiceTextChange}
                    variant="outlined"
                    fullWidth
                    placeholder="Michelin Road 5 180/55ZR17 73W 10 €117.58 €1,175.80&#10;Pirelli Rosso IV 120/70ZR17 58W 30 €97.67 €2,930.10&#10;..."
                    style={{ marginBottom: 12, fontFamily: "monospace", fontSize: "0.9rem" }}
                    disabled={parserBusy}
                  />
                  
                  <Box className={classes.parserActions}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={this.parseInvoiceText}
                      disabled={!invoiceText.trim() || parserBusy}
                      startIcon={parsingInvoice ? <CircularProgress size={16} /> : <AddIcon />}
                    >
                      {parsingInvoice ? hebrewLabels.messages.parsingInvoice : hebrewLabels.buttons.parseInvoice}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={this.clearInvoiceText}
                      disabled={!invoiceText.trim() || parserBusy}
                    >
                      {hebrewLabels.buttons.clearInvoiceText}
                    </Button>
                  </Box>
                </Paper>
              </Box>
            )}

            <Typography variant="h6" gutterBottom style={{ marginTop: 16 }}>
              {hebrewLabels.fields.rows}
            </Typography>

            <Box className={classes.rowsTable}>
              {currentInvoice.rows.map((row, index) => (
                <Paper key={index} style={{ padding: 16, marginBottom: 16 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12}>
                      <Box style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <Typography variant="caption" color="textSecondary">
                          שורה {row.line}
                        </Typography>
                        {row.autoMatched && row.eceId && (
                          <span className={classes.autoMatchBadge}>התאמה אוטומטית ✓</span>
                        )}
                      </Box>
                      {row.matchReason && (
                        <Typography className={classes.matchReason}>
                          {row.matchReason}
                        </Typography>
                      )}
                    </Grid>
                    
                    {/* Certificate Selector */}
                    <Grid item xs={12}>
                      {isViewMode ? (
                        <>
                          {this.renderCurrentCertificate(
                            availableCertificates.find(
                              (cert) => cert.eceId === row.eceId
                            )
                          )}
                        </>
                      ) : (
                        <>
                          {/* Show currently selected certificate */}
                          {row.eceId && this.renderCurrentCertificate(
                            availableCertificates.find(
                              (cert) => cert.eceId === row.eceId
                            )
                          )}
                          
                          {/* Autocomplete with search */}
                          <Autocomplete
                            options={availableCertificates}
                            getOptionLabel={(option) =>
                              `${option.eceId} - ${option.brand} ${formatCertificateSizes(option)}`
                            }
                            getOptionSelected={(option, value) => 
                              option.eceId === value.eceId
                            }
                            value={
                              availableCertificates.find(
                                (cert) => cert.eceId === row.eceId
                              ) || null
                            }
                            onChange={(event, newValue) => {
                              this.handleCertificateSelect(index, newValue);
                            }}
                            renderOption={(option, state) => 
                              this.renderCertificateOption(option, state)
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="חיפוש ובחירת תעודת ECE"
                                placeholder="הקלד לחיפוש: מספר ECE, יצרן, מידה או שם דגם..."
                                variant="outlined"
                                size="small"
                                helperText="חפש לפי מספר תעודה, יצרן (Michelin, Pirelli...), מידה או שם דגם"
                                InputProps={{
                                  ...params.InputProps,
                                  startAdornment: (
                                    <>
                                      <SearchIcon style={{ color: "rgba(0, 0, 0, 0.54)", marginLeft: 8 }} />
                                      {params.InputProps.startAdornment}
                                    </>
                                  ),
                                  endAdornment: (
                                    <>
                                      {certificatesLoading ? (
                                        <CircularProgress size={20} />
                                      ) : null}
                                      {params.InputProps.endAdornment}
                                    </>
                                  ),
                                }}
                              />
                            )}
                            filterOptions={(options, { inputValue }) => {
                              if (!inputValue) return options;
                              const searchTerm = inputValue.toLowerCase();
                              return options.filter(
                                (option) =>
                                  option.eceId.toLowerCase().includes(searchTerm) ||
                                  option.brand.toLowerCase().includes(searchTerm) ||
                                  formatCertificateSizes(option).toLowerCase().includes(searchTerm) ||
                                  (option.goodfor &&
                                    option.goodfor.some((model) =>
                                      model.toLowerCase().includes(searchTerm)
                                    ))
                              );
                            }}
                            loading={certificatesLoading}
                            disabled={isViewMode}
                            fullWidth
                            openOnFocus
                            clearOnBlur={false}
                            selectOnFocus
                            autoHighlight
                          />
                        </>
                      )}
                    </Grid>
                    
                    {/* Product Name */}
                    <Grid item xs={12} md={isViewMode ? 12 : 6}>
                      <TextField
                        label={hebrewLabels.fields.name}
                        value={row.name}
                        onChange={this.handleRowFieldChange(index, "name")}
                        size="small"
                        fullWidth
                        disabled={isViewMode}
                      />
                    </Grid>
                    
                    {/* Size */}
                    <Grid item xs={12} md={isViewMode ? 12 : 6}>
                      <TextField
                        label={hebrewLabels.fields.size}
                        value={row.size}
                        onChange={this.handleRowFieldChange(index, "size")}
                        size="small"
                        fullWidth
                        disabled={isViewMode}
                      />
                    </Grid>
                    
                    {/* Delete Button */}
                    {!isViewMode && (
                      <Grid item xs={12}>
                        <Button
                          variant="outlined"
                          color="secondary"
                          startIcon={<DeleteIcon />}
                          onClick={() => this.removeRow(index)}
                          size="small"
                        >
                          {hebrewLabels.buttons.removeRow}
                        </Button>
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              ))}
            </Box>

            {!isViewMode && (
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={this.addRow}
                className={classes.addRowButton}
              >
                {hebrewLabels.buttons.addRow}
              </Button>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={this.closeDialog}>
              {isViewMode ? "סגור" : hebrewLabels.buttons.cancel}
            </Button>
            {!isViewMode && (
              <Button
                onClick={this.handleSave}
                color="primary"
                variant="contained"
                disabled={parserBusy}
              >
                {hebrewLabels.buttons.save}
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={this.closeDeleteDialog}>
          <DialogTitle>{hebrewLabels.dialog.delete}</DialogTitle>
          <DialogContent>
            <Typography>{hebrewLabels.dialog.deleteConfirm}</Typography>
            {invoiceToDelete && (
              <Typography variant="body2" style={{ marginTop: 16 }}>
                מספר חשבונית: {invoiceToDelete.invoiceId}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={this.closeDeleteDialog}>
              {hebrewLabels.buttons.cancel}
            </Button>
            <Button onClick={this.handleDelete} color="secondary" variant="contained">
              {hebrewLabels.buttons.delete}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={this.closeSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert onClose={this.closeSnackbar} severity={snackbar.severity}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </div>
    );
  }
}

EceInvoicesManager.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(EceInvoicesManager);

